import { useState } from 'react';
import { Card, Form, Input, Button, Typography, Alert } from 'antd';
import { MailOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useRequestPasswordReset, useConfirmPasswordReset } from './hooks';
import { ApiError } from '@starling/core/api/errors';

const { Title, Text } = Typography;

// ── Общая центрирующая обёртка (mist-фон, borderless-карточка по центру) —
// как в LoginPage / AcceptInvitePage / ContextPickerPage.
function AuthShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--mist)',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 420 }} variant="borderless">
        {children}
      </Card>
    </div>
  );
}

// ── Иконка-бейдж над заголовком (зеркало ExpiredCard из ContextPickerPage).
function IconBadge({
  tone,
  children,
}: {
  tone: 'accent' | 'warning';
  children: React.ReactNode;
}): JSX.Element {
  const accent = tone === 'accent';
  return (
    <span
      style={{
        width: 48,
        height: 48,
        borderRadius: 'var(--radius-md)',
        margin: '0 auto 14px',
        background: accent ? 'var(--accent-soft)' : '#FCEEDB',
        color: accent ? 'var(--health-turquoise-600)' : 'var(--warning)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 24,
      }}
    >
      {children}
    </span>
  );
}

// ── Заголовок карточки (Title + secondary), по центру.
function Head({ title, subtitle }: { title: string; subtitle?: string }): JSX.Element {
  return (
    <div style={{ textAlign: 'center', marginBottom: 20 }}>
      <Title level={3} style={{ marginBottom: subtitle ? 6 : 0 }}>
        {title}
      </Title>
      {subtitle && <Text type="secondary">{subtitle}</Text>}
    </div>
  );
}

// ── Ссылка «Назад ко входу» снизу карточки.
function BackToLogin(): JSX.Element {
  const { t } = useTranslation();
  return (
    <div style={{ textAlign: 'center', marginTop: 20 }}>
      <Link to="/login">{t('auth.password_reset.back_to_login')}</Link>
    </div>
  );
}

interface RequestValues {
  email: string;
}

/**
 * Экран 1 — «Забыли пароль?» (public `/forgot-password`).
 *
 * Email → `POST /auth/password-reset/request`. Ответ **всегда 200**
 * (анти-enumeration) → всегда показываем «Проверьте почту», не раскрывая,
 * существует ли аккаунт. Реальный не-200 = сеть/инфраструктура → alert.
 */
export function ForgotPasswordPage(): JSX.Element {
  const { t } = useTranslation();
  const [form] = Form.useForm<RequestValues>();
  const mutation = useRequestPasswordReset();
  const [sentEmail, setSentEmail] = useState<string | null>(null);
  const [networkError, setNetworkError] = useState(false);

  const onFinish = (values: RequestValues): void => {
    const email = values.email.trim();
    setNetworkError(false);
    mutation.mutate(
      // product: бренд/From письма и база ссылки — профиль Light (доводка V1 №15).
      { email, product: 'light' },
      {
        onSuccess: () => setSentEmail(email),
        onError: () => setNetworkError(true),
      },
    );
  };

  // Состояние «письмо отправлено» заменяет форму целиком: одинаковый текст
  // для существующего и несуществующего email.
  if (sentEmail !== null) {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <IconBadge tone="accent">
            <MailOutlined />
          </IconBadge>
        </div>
        <Head title={t('auth.password_reset.request.sent_title')} />
        <div style={{ textAlign: 'center', marginBottom: 4 }}>
          <Text type="secondary">{t('auth.password_reset.request.sent_description')}</Text>
        </div>
        {sentEmail && (
          <div style={{ textAlign: 'center', marginTop: 8 }}>
            <Text strong className="sl-mono" style={{ fontSize: 13 }}>
              {sentEmail}
            </Text>
          </div>
        )}
        <BackToLogin />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Head
        title={t('auth.password_reset.request.title')}
        subtitle={t('auth.password_reset.request.subtitle')}
      />
      {networkError && (
        <Alert
          type="error"
          showIcon
          role="alert"
          style={{ marginBottom: 16 }}
          message={t('auth.password_reset.request.error_network')}
        />
      )}
      <Form<RequestValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        disabled={mutation.isPending}
      >
        <Form.Item
          label={t('auth.password_reset.request.email')}
          name="email"
          rules={[
            { required: true, message: t('auth.password_reset.request.email_required') },
            { type: 'email', message: t('auth.password_reset.request.email_invalid') },
          ]}
        >
          <Input autoComplete="username" placeholder="you@optika.kz" autoFocus />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" block loading={mutation.isPending} size="large">
            {t('auth.password_reset.request.submit')}
          </Button>
        </Form.Item>
      </Form>
      <BackToLogin />
    </AuthShell>
  );
}

interface ConfirmValues {
  password: string;
  password_confirm: string;
}

/**
 * Экран 2 — «Новый пароль» (public `/password-reset?token=…`).
 *
 * Токен из query. Пароль + повтор (клиентская валидация min 8 / совпадение) →
 * `POST /auth/password-reset/confirm`:
 *   * 204 → «Пароль изменён» → «Войти».
 *   * 400 `reset_token_invalid` → «Ссылка недействительна» + «Запросить новую».
 *   * 422 → ошибка под полем пароля.
 * Нет `token` в URL → сразу состояние «ссылка недействительна».
 */
export function PasswordResetPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm<ConfirmValues>();
  const token = searchParams.get('token');

  const mutation = useConfirmPasswordReset();
  // Нет токена в URL — ссылка заведомо битая, показываем «недействительна» сразу.
  const [phase, setPhase] = useState<'form' | 'done' | 'invalid'>(token ? 'form' : 'invalid');
  const [networkError, setNetworkError] = useState(false);

  const onFinish = (values: ConfirmValues): void => {
    if (!token) {
      setPhase('invalid');
      return;
    }
    setNetworkError(false);
    mutation.mutate(
      { token, new_password: values.password },
      {
        onSuccess: () => setPhase('done'),
        onError: (err) => {
          if (err instanceof ApiError) {
            // Одноразовый токен: не найден / протух / уже использован.
            if (err.status === 400 && err.code === 'reset_token_invalid') {
              setPhase('invalid');
              return;
            }
            // Валидация пароля на бэке (min 8 / pattern) — под поле пароля.
            if (err.status === 422) {
              form.setFields([
                {
                  name: 'password',
                  errors: [err.message || t('auth.password_reset.confirm.password_min_length')],
                },
              ]);
              return;
            }
          }
          setNetworkError(true);
        },
      },
    );
  };

  if (phase === 'done') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <IconBadge tone="accent">
            <CheckCircleOutlined />
          </IconBadge>
        </div>
        <Head
          title={t('auth.password_reset.confirm.done_title')}
          subtitle={t('auth.password_reset.confirm.done_description')}
        />
        <Button
          type="primary"
          block
          size="large"
          onClick={() => navigate('/login', { replace: true })}
        >
          {t('auth.password_reset.confirm.done_submit')}
        </Button>
      </AuthShell>
    );
  }

  if (phase === 'invalid') {
    return (
      <AuthShell>
        <div style={{ textAlign: 'center' }}>
          <IconBadge tone="warning">
            <ClockCircleOutlined />
          </IconBadge>
        </div>
        <Head
          title={t('auth.password_reset.confirm.invalid_title')}
          subtitle={t('auth.password_reset.confirm.invalid_description')}
        />
        <Button
          type="primary"
          block
          size="large"
          onClick={() => navigate('/forgot-password', { replace: true })}
        >
          {t('auth.password_reset.confirm.invalid_submit')}
        </Button>
        <BackToLogin />
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <Head
        title={t('auth.password_reset.confirm.title')}
        subtitle={t('auth.password_reset.confirm.subtitle')}
      />
      {networkError && (
        <Alert
          type="error"
          showIcon
          role="alert"
          style={{ marginBottom: 16 }}
          message={t('auth.password_reset.confirm.error_network')}
        />
      )}
      <Form<ConfirmValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        disabled={mutation.isPending}
      >
        <Form.Item
          label={t('auth.password_reset.confirm.password')}
          name="password"
          rules={[
            { required: true, message: t('auth.password_reset.confirm.password_required') },
            { min: 8, message: t('auth.password_reset.confirm.password_min_length') },
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            placeholder={t('auth.password_reset.confirm.password_placeholder')}
            autoFocus
          />
        </Form.Item>
        <Form.Item
          label={t('auth.password_reset.confirm.password_confirm')}
          name="password_confirm"
          dependencies={['password']}
          rules={[
            { required: true, message: t('auth.password_reset.confirm.password_required') },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  new Error(t('auth.password_reset.confirm.password_mismatch')),
                );
              },
            }),
          ]}
        >
          <Input.Password
            autoComplete="new-password"
            placeholder={t('auth.password_reset.confirm.password_confirm_placeholder')}
          />
        </Form.Item>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" block loading={mutation.isPending} size="large">
            {t('auth.password_reset.confirm.submit')}
          </Button>
        </Form.Item>
      </Form>
      <BackToLogin />
    </AuthShell>
  );
}
